import React, {Component} from 'react';
import PropTypes from 'prop-types';
import * as Api from 'utils/api/api.js';
import {connect} from 'react-redux';
import * as reduxSelectors from 'components/store/application.reducers';
import _ from 'lodash';
import {
    getTaskDetails,
    hasEditedDocument,
    updateDocumentVersion,
    getTaskDocumentData,
    integrationIframeHasUnsavedChanges,
    iframeHasVBNXTFlag
} from 'components/taskdetails-remake/taskDetails.action';
import {DETAILS_VIEW_TYPE} from 'utils/constants';
import classNames from "classnames";
import {updateDocumentKey} from "components//documentoverview/documentOverview.action";
import {handleError} from 'utils/errorHandle.function';
import {snowplowTrackEvent} from "utils/snowplow";
import {PATH_TO_DOCUMENT_OVERVIEW_DETAILS} from "components/router/paths.js";
import {setTaskWaitingWSUpdate} from "components/mytasks/myTasks.action.js";
import {launchDarkly} from "utils/launchDarkly.ts";
import { Loader, LoaderSize } from "components/common/Loader.component.tsx";
import { CircleAlert } from "lucide-react";

const CONFIRMATION = "CONFIRMATION";

export class DocumentEditor extends Component {
    constructor(props) {
        super(props);
        this.state = {
            documentVersion: null,
            editor: null,
            editorLoadingError: false,
            editorState: null,
            editing: false,
            iframeLoadStart: (+new Date())
        };
        this.requests = [];
        this.receiveMessage = this.receiveMessage.bind(this);
        this.stopEditing = this.stopEditing.bind(this);
        this.onLoadIframe = this.onLoadIframe.bind(this);
        this.sendMessageToEditor = this.sendMessageToEditor.bind(this);
        this.editorRef = React.createRef();
        this.editorIframeRef = React.createRef();

        this.useWaitForCompello = launchDarkly.userClient?.variation("fe-wait-for-compello", false);
    }

    initialize() {
        const loadTimeStart = +new Date();

        const apiCallMethod = this.props.viewType === DETAILS_VIEW_TYPE.DOCUMENT ? Api.getDocumentOverviewEditor : Api.getDocumentEditor;

        return apiCallMethod(this.props.taskId, this.props.companyId).then((response) => {
            const loadTime = (+new Date()) - loadTimeStart;

            const companyId = (this.props.viewType === DETAILS_VIEW_TYPE.TASK ? this.props.companyId : this.props.userConfiguration.currentContext) + "";
            const editRequest = {
                companyId,
                systemId: this.props.externalAppId + "",
                userId: this.props.userConfiguration.currentUserId + "",
                readonly: this.props.isReadOnly, //history task open editor in view mode only
                administrator: this.props.userConfiguration.userRoles.systemAdministrator,
                taskId: this.props.id
            };

            snowplowTrackEvent({
                category: "document_editor",
                action: "get_external_editor",
                label: "execution_time",
                value: loadTime,
                property: this.props.externalApplicationName
            }, this.props.userConfiguration);

            this.setState({
                editor: _.template(response.url)({
                    editRequest: encodeURIComponent(JSON.stringify(editRequest))
                }),
                editorLoadingError: false
            }, () => {
                console.log(this.editorRef.current);
            });
        }).catch(() => {
            this.setState({
                editorLoadingError: true
            });
        });
    }

    componentDidMount() {
        window.addEventListener("message", this.receiveMessage, false);

        this.requests.push(this.initialize());
    }

    componentDidUpdate(prevProps) {
        if (prevProps.taskId !== this.props.taskId) {
            this.setState({
                documentVersion: null,
                editor: null,
                editorLoadingError: false,
                editorState: null,
                editing: false,
                iframeLoadStart: (+new Date())
            });

            this.requests.push(this.initialize());
        }
    }

    sendMessageToEditor(message) {
        //send message just for compello editor
        if (this.state.editor?.includes("compello")) {
            this.editorIframeRef.current.contentWindow.postMessage({event: message}, this.state.editor);
        }
    }


    stopEditing(version) {
        let skipWorklowRestart = CONFIRMATION === this.props.reapproveType;

        window.removeEventListener("beforeunload", this.preventWindowClose);
        window.removeEventListener('unload', this.preventWindowClose);

        //this needs to happen here because of the navigation blocker in task details
        this.props.integrationIframeHasUnsavedChanges(false);


        if (this.props.viewType === DETAILS_VIEW_TYPE.DOCUMENT) {
            Api.updateDocument(this.props.taskId, version, skipWorklowRestart, this.props.companyId).then(response => {
                this.props.updateDocumentKey(this.props.taskId, response.result);
                this.props.route(PATH_TO_DOCUMENT_OVERVIEW_DETAILS + response.result, {replace: true});
            }, error => {
                handleError(error);
            });
        } else {
            this.props.updateDocumentVersion(version);
            Api.updateTask(this.props.taskId, version, skipWorklowRestart).then(() => {
                this.props.getTaskDetails(this.props.taskId, this.props.isCompelloTask);
                this.props.getTaskDocumentData(this.props.taskId, this.props.viewType === DETAILS_VIEW_TYPE.DOCUMENT);
            }, error => {
                handleError(error);
            });
        }
    }

    onLoadIframe() {
        const loadTime = (+new Date()) - this.state.iframeLoadStart;

        snowplowTrackEvent({
            category: "document_editor",
            action: "load_external_editor_iframe",
            label: "execution_time",
            value: loadTime,
            property: this.props.externalApplicationName
        }, this.props.userConfiguration);
    }

    componentWillUnmount() {
        this.requests.forEach(p => p.cancel());
        window.removeEventListener("message", this.receiveMessage);
        window.removeEventListener("beforeunload", this.preventWindowClose);
        window.removeEventListener('unload', this.preventWindowClose);
    }

    preventWindowClose = (event) => {
        event.preventDefault();
        event.returnValue = true;
        return "";
    }

    receiveMessage(event) {
        const {editor} = this.state;

        if (editor && event.origin && editor.startsWith(event.origin)) {
            switch (event.data.event) {
                case "ready":
                case "viewing":
                    this.handleReadyOrViewingEvent(event);
                    break;

                case "editing":
                    this.handleEditingEvent(event);
                    break;

                default:
                    break;
            }
        }
    }

    handleReadyOrViewingEvent(event) {
        const {editing, documentVersion} = this.state;

        if (!this.props.isReadOnly && editing) {
            if (event.data.version && event.data.version > documentVersion) {
                if (this.useWaitForCompello && this.props.isCompelloTask) {
                    document.getElementById("task-details-wrapper")?.focus();
                    this.props.setTaskWaitingWSUpdate(this.props.taskId);
                }
                this.updateEditorState(event.data.version, false, event.data.event);
                this.stopEditing(event.data.version);
                this.props.hasEditedDocument();
                return;
            } else {
                this.props.integrationIframeHasUnsavedChanges(false);
            }
        }

        this.updateEditorState(event.data.version, false, event.data.event);
        this.props.integrationIframeHasUnsavedChanges(false);
    }

    handleEditingEvent(event) {
        const vbnxtFlag = event.data.supportsSaveAndDiscard;

        this.props.iframeHasVBNXTFlag(vbnxtFlag);
        this.props.integrationIframeHasUnsavedChanges(true);

        window.addEventListener("beforeunload", this.preventWindowClose);
        window.addEventListener("unload", this.preventWindowClose);

        this.setState({
            editorState: event.data.event,
            editing: true
        });
    }

    updateEditorState(version, editing, state) {
        this.setState({
            documentVersion: version,
            editing,
            editorState: state
        });
    }


    render() {
        return (
            <div className="height-100" ref={this.editorRef}>
                {this.state.editor === null && !this.state.editorLoadingError &&
                    <div className={classNames("document-editor-frame height-100",
                        {"document-editor-popup": this.props.isPopup})}>
                        <div className="position-absolute top-50 w-100">
                            <Loader size={LoaderSize.Medium} />
                        </div>
                    </div>
                }
                {this.state.editorLoadingError &&
                    <div className={classNames("col-md-12 document-editor-frame height-100",
                        {"document-editor-popup": this.props.isPopup})}>
                        <EditorError translate={this.props.translate}/>
                    </div>
                }

                {(this.state.editor !== null) && !this.state.editorLoadingError &&
                    <iframe
                        ref={this.editorIframeRef}
                        id={"Document-Editor-Iframe"}
                        src={this.state.editor}
                        title={"Document Editor"}
                        onLoad={this.onLoadIframe}
                        {...this.props.externalApplicationName === "Visma Business Cloud" && {allow: "clipboard-read; clipboard-write"}}
                        className={
                            classNames("w-100 height-100 document-editor-frame",
                                {"document-editor-popup": this.props.isPopup})
                        }
                    >
                    </iframe>
                }
            </div>
        );
    }
}

const
    mapStateToProps = function (store) {
        return {
            userConfiguration: reduxSelectors.getUsersData(store)
        };
    };

const
    connected = connect(mapStateToProps, {
        getTaskDetails,
        hasEditedDocument,
        updateDocumentKey,
        updateDocumentVersion,
        getTaskDocumentData,
        integrationIframeHasUnsavedChanges,
        iframeHasVBNXTFlag,
        setTaskWaitingWSUpdate
    })(DocumentEditor);

export default connected;

DocumentEditor
    .propTypes = {
    taskId: PropTypes.string, // json for one task
    translate: PropTypes.func, // to translate texts
};

const
    EditorError = (props) => {
        return (
            <div className="error-message text-center">
                <CircleAlert size={48} className="pull-left" style={{ color: "var(--ga-color-icon-error)" }}/>
                <span
                    className="col-md-10 text-left margin-bottom"><b>{props.translate("popUp.documentEditor.errorTitle")}</b></span>
                <br/>
                <span className="col-md-10 text-left">{props.translate("popUp.documentEditor.errorSuggestion")}</span>
            </div>)
    };